<template>
  <div class="pb-page tm">
    <PageHeader title="Team & approvals" subtitle="Teammates sign in with their own login. Clients approve testimonials through a private link, no account needed." />

    <section v-if="can('members')" class="tm-section" aria-labelledby="tm-team">
      <div class="pb-card-head stacks">
        <div><h2 id="tm-team" class="pb-section-title display-font">Teammates</h2>
          <p class="pb-muted">{{ seatText }}</p></div>
        <LockedAction v-if="inviteCap" feature="seats" look="pb-button" icon="mdi-account-plus-outline" label="Invite" :cap="inviteCap" :used="seatsUsed" />
        <button v-else type="button" class="pb-button" @click="openInvite"><i class="mdi mdi-account-plus-outline pb-ico" aria-hidden="true"></i>Invite</button>
      </div>
      <LockedPanel v-if="oneSeat" feature="seats" title="Team seats" :text="seatsLine" />
      <div v-else class="pb-card tm-list-card">
        <div class="tm-member owner">
          <span class="pb-avatar" aria-hidden="true">{{ initials(team.owner ? team.owner.name || team.owner.email : '') }}</span>
          <div class="tm-who"><strong>{{ team.owner ? (team.owner.name || team.owner.email) : '' }}</strong><span class="pb-muted">{{ team.owner ? team.owner.email : '' }} · Owner</span></div>
        </div>
        <div v-for="member in team.members" :key="member.id" class="tm-member">
          <span class="pb-avatar" aria-hidden="true">{{ initials(member.name || member.email) }}</span>
          <div class="tm-who"><strong>{{ member.name || member.email }}</strong>
            <span class="pb-muted">{{ member.email }} · {{ member.status === 'invited' ? (member.invite_expired ? 'Invitation expired' : 'Invited') : roleLabel(member.role) }} · {{ scopeText(member) }}</span></div>
          <div class="pb-actions">
            <label class="pb-sr-only" :for="'tm-role-' + member.id">Role for {{ member.email }}</label>
            <select :id="'tm-role-' + member.id" class="pb-select tm-role" :value="member.role" @change="changeRole(member, $event.target.value)">
              <option value="admin">Admin</option><option value="editor">Editor</option></select>
            <button v-if="member.status === 'invited'" type="button" class="pb-button sm outline" @click="reinvite(member)">New invite link</button>
            <button type="button" class="pb-button sm ghost" @click="removeMember(member)">Remove</button>
          </div>
        </div>
        <p v-if="!team.members.length" class="pb-muted tm-none">Invite a teammate. They sign up with their own login and see the brands you choose.</p>
      </div>
      <details v-if="!oneSeat" class="tm-roles"><summary>What can each role do?</summary>
        <ul><li><strong>Admin:</strong> everything in their brands except teammates and billing, including brands, client reviewers, report links, templates and export.</li>
          <li><strong>Editor:</strong> the inbox, testimonials, imports, forms, widgets, the wall, quote graphics and reports in their brands.</li></ul></details>
    </section>
    <div v-else-if="ws && ws.owner_email" class="pb-note"><i class="mdi mdi-account-supervisor-outline pb-ico" aria-hidden="true"></i><span>Teammates are managed by {{ ws.owner_email }}.</span></div>

    <section v-if="needsAgencyName" class="pb-card tm-agency" aria-labelledby="tm-agency-title">
      <h2 id="tm-agency-title" class="pb-section-title display-font">Add your agency's name</h2>
      <p class="pb-muted">Your clients see it on their approval and report pages, so add it before you send client links.</p>
      <form v-if="can('workspace')" class="tm-agency-form" @submit.prevent="saveAgencyName">
        <label class="pb-label" for="tm-agency-name">Agency name</label>
        <div class="pb-row"><input id="tm-agency-name" v-model="agencyName" class="pb-input" maxlength="120" placeholder="e.g. Fernhollow Studio" autocomplete="organization">
          <button type="submit" class="pb-button" :disabled="busy || !agencyName.trim()">Save</button></div>
        <p class="pb-help">You can add your agency's logo in <router-link to="/settings">Settings</router-link>.</p>
      </form>
      <p v-else class="pb-body">Ask {{ ws && ws.owner_email ? ws.owner_email : 'the workspace owner' }} to add it in Settings.</p>
    </section>

    <section v-if="can('reviewers')" class="tm-section" aria-labelledby="tm-clients">
      <div class="pb-card-head stacks">
        <div><h2 id="tm-clients" class="pb-section-title display-font">Client approvals · {{ brand ? brand.name : '' }}</h2>
          <p class="pb-muted">Your client sees only this brand's waiting testimonials, with your agency's name and logo.</p></div>
        <button v-if="limits.client_reviewers" type="button" class="pb-button outline" :disabled="reviewers.length >= 5 || needsAgencyName" @click="reviewerOpen = true"><i class="mdi mdi-account-check-outline pb-ico" aria-hidden="true"></i>Add client reviewer</button>
        <LockedAction v-else feature="client_reviewers" icon="mdi-account-check-outline" label="Add client reviewer" />
      </div>
      <LockedPanel v-if="!limits.client_approvals" feature="client_approvals" title="Client approvals and restricted reviewers" :text="approvalsLine" />
      <div v-else class="pb-card tm-list-card">
        <label class="pb-check tm-approval"><input type="checkbox" :checked="brand && brand.requires_client_approval" @change="toggleApproval($event)">Require client approval before publishing</label>
        <div v-for="reviewer in reviewers" :key="reviewer.id" class="tm-member">
          <span class="pb-avatar" aria-hidden="true">{{ initials(reviewer.name) }}</span>
          <div class="tm-who"><strong>{{ reviewer.name }}</strong><span class="pb-muted">{{ reviewer.email || 'No email' }} · {{ reviewer.last_seen_at ? 'Last opened ' + when(reviewer.last_seen_at) : 'Not opened yet' }}</span></div>
          <div class="pb-actions">
            <button type="button" class="pb-button sm outline" @click="rotate(reviewer)">New link</button>
            <button type="button" class="pb-button sm ghost" @click="revoke(reviewer)">Remove</button>
          </div>
        </div>
        <p v-if="!reviewers.length" class="pb-muted tm-none">Add your client as a reviewer to send them an approval link.</p>
        <div v-if="freshLink" class="tm-fresh"><SnippetBox :value="freshLink.url" :label="freshLink.label" @copied="copied" />
          <p class="pb-help">Copy it now: for security we only show a link once. You can always make a new one.</p></div>
      </div>
    </section>

    <section v-if="can('report_links')" class="tm-section" aria-labelledby="tm-reports">
      <div class="pb-card-head">
        <div><h2 id="tm-reports" class="pb-section-title display-font">Client report links · {{ brand ? brand.name : '' }}</h2>
          <p class="pb-muted">A read-only page with the last 90 days and recent testimonials, for your client.</p></div>
        <LockedAction v-if="!limits.client_reports" feature="client_reports" icon="mdi-link-plus" label="Create client report link" />
      </div>
      <LockedPanel v-if="!limits.client_reports" feature="client_reports" />
      <div v-else class="pb-card tm-list-card">
        <form v-if="links.length < 5" class="tm-link-form" @submit.prevent="createLink">
          <label class="pb-label" for="tm-link-label">New report link</label>
          <div class="pb-row"><input id="tm-link-label" v-model="linkLabel" class="pb-input" maxlength="120" placeholder="e.g. Monthly report for the client">
            <button type="submit" class="pb-button outline" :disabled="!linkLabel.trim() || needsAgencyName"><i class="mdi mdi-link-plus pb-ico" aria-hidden="true"></i>Create</button></div>
        </form>
        <div v-for="link in links" :key="link.id" class="tm-member">
          <span class="tm-link-icon" aria-hidden="true"><i class="mdi mdi-chart-box-outline"></i></span>
          <div class="tm-who"><strong>{{ link.label }}</strong><span class="pb-muted">Created {{ when(link.created_at) }}</span></div>
          <div class="pb-actions"><button type="button" class="pb-button sm ghost" @click="revokeLink(link)">Revoke</button></div>
        </div>
        <p v-if="!links.length" class="pb-muted tm-none">No report links yet.</p>
        <div v-if="freshReport" class="tm-fresh"><SnippetBox :value="freshReport.url" label="Report link" @copied="copied" />
          <p class="pb-help">Copy it now: for security we only show a link once.</p></div>
      </div>
    </section>

    <section v-if="can('templates')" class="tm-section" aria-labelledby="tm-templates">
      <h2 id="tm-templates" class="pb-section-title display-font tm-h">Saved templates</h2>
      <LockedPanel v-if="!limits.agency_templates" feature="agency_templates" />
      <div v-else class="pb-card tm-list-card">
        <div v-for="item in templates" :key="item.id" class="tm-member">
          <span class="tm-link-icon" aria-hidden="true"><i :class="['mdi', item.kind === 'form' ? 'mdi-form-select' : 'mdi-widgets-outline']"></i></span>
          <div class="tm-who"><strong>{{ item.name }}</strong><span class="pb-muted">{{ item.kind === 'form' ? 'Collection page' : 'Widget' }} · {{ item.summary }}</span></div>
          <div class="pb-actions"><button type="button" class="pb-button sm ghost" @click="deleteTemplate(item)">Delete</button></div>
        </div>
        <p v-if="!templates.length" class="pb-muted tm-none">Save a collection page or a widget as a template from its menu, then reuse it for every client.</p>
      </div>
    </section>

    <!-- Brand-level access lives in the invitation; on plans without it, this section says what it does. -->
    <section v-if="can('members') && !limits.brand_scoped_seats" class="tm-section" aria-labelledby="tm-access">
      <h2 id="tm-access" class="pb-section-title display-font tm-h">Brand-level access</h2>
      <LockedPanel feature="brand_scoped_seats" />
    </section>

    <v-dialog v-model="inviteOpen" max-width="560">
      <form class="pb-dialog" @submit.prevent="invite">
        <div class="pb-dialog-head"><h2>Invite a teammate</h2>
          <button type="button" class="pb-icon-button" aria-label="Close" @click="inviteOpen = false"><i class="mdi mdi-close" aria-hidden="true"></i></button></div>
        <template v-if="!inviteLink">
          <div class="pb-field"><label class="pb-label" for="tm-email">Their email address</label><input id="tm-email" v-model="inviteForm.email" type="email" class="pb-input" maxlength="191" required></div>
          <div class="pb-field"><label class="pb-label" for="tm-new-role">Role</label>
            <select id="tm-new-role" v-model="inviteForm.role" class="pb-select"><option value="editor">Editor</option><option value="admin">Admin</option></select></div>
          <div v-if="limits.brand_scoped_seats" class="pb-field"><span class="pb-label">Brands they can see</span>
            <label class="pb-check"><input v-model="inviteForm.brand_scope" type="radio" value="all">All brands</label>
            <label class="pb-check tm-gap"><input v-model="inviteForm.brand_scope" type="radio" value="selected">Only these:</label>
            <div v-if="inviteForm.brand_scope === 'selected'" class="tm-brands">
              <label v-for="item in brands" :key="item.id" class="pb-check"><input v-model="inviteForm.brand_ids" type="checkbox" :value="item.id">{{ item.name }}</label></div></div>
          <div v-else class="pb-field"><span class="pb-label">Brands they can see</span>
            <p class="pb-help tm-scope">All brands. <LockedAction feature="brand_scoped_seats" look="pb-button link" label="Only the brands you choose" @upgrade="upsell" /></p></div>
          <div v-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span></div>
          <div class="pb-dialog-actions"><button type="button" class="pb-button ghost" @click="inviteOpen = false">Cancel</button>
            <button type="submit" class="pb-button" :disabled="busy">{{ busy ? 'Creating…' : 'Create invitation' }}</button></div>
        </template>
        <template v-else>
          <p>Send this link to {{ inviteForm.email }}. It works for 14 days, and they must sign in with that address.</p>
          <SnippetBox :value="inviteLink" label="Invitation link" @copied="copied" />
          <div class="pb-dialog-actions"><button type="button" class="pb-button" @click="inviteOpen = false">Done</button></div>
        </template>
      </form>
    </v-dialog>

    <v-dialog v-model="reviewerOpen" max-width="520">
      <form class="pb-dialog" @submit.prevent="addReviewer">
        <div class="pb-dialog-head"><h2>Add a client reviewer</h2>
          <button type="button" class="pb-icon-button" aria-label="Close" @click="reviewerOpen = false"><i class="mdi mdi-close" aria-hidden="true"></i></button></div>
        <p>They get a private link to approve or reject {{ possessive(brand ? brand.name : 'this brand') }} testimonials. No account needed.</p>
        <div class="pb-field"><label class="pb-label" for="tm-r-name">Name</label><input id="tm-r-name" v-model="reviewerForm.name" class="pb-input" maxlength="120" required></div>
        <div class="pb-field"><label class="pb-label" for="tm-r-email">Email <span class="optional">(optional, for your records)</span></label><input id="tm-r-email" v-model="reviewerForm.email" type="email" class="pb-input" maxlength="191"></div>
        <div v-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span></div>
        <div class="pb-dialog-actions"><button type="button" class="pb-button ghost" @click="reviewerOpen = false">Cancel</button>
          <button type="submit" class="pb-button" :disabled="busy || !reviewerForm.name.trim()">Add reviewer</button></div>
      </form>
    </v-dialog>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" :stacked="inviteOpen || reviewerOpen" />
  </div>
</template>

<script>
import LockedAction from '../components/LockedAction.vue';
import LockedPanel from '../components/LockedPanel.vue';
import PageHeader from '../components/PageHeader.vue';
import SnippetBox from '../components/SnippetBox.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import workspace from '../mixins/workspace';
import { get, patch, post, remove, message, planLimit } from '../api';
import { initials } from '../lib/color';
import { formatRelative, possessive, withStop } from '../lib/format';
import { ask } from '../lib/confirm';
import { toast } from '../lib/toast';
import { LOCKED_FEATURES, allowsMore, planOrder } from '../lib/plans';
import { limitReached } from '../lib/upgrades';

export default {
  name: 'TeamView',
  components: { LockedAction, LockedPanel, PageHeader, SnippetBox, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ team: { owner: null, members: [], seats: {} }, reviewers: [], links: [], templates: [], freshLink: null, freshReport: null,
    inviteOpen: false, inviteForm: { email: '', role: 'editor', brand_scope: 'all', brand_ids: [] }, inviteLink: '',
    reviewerOpen: false, reviewerForm: { name: '', email: '' }, linkLabel: '', busy: false, error: '', upgrade: null, upgradeOpen: false,
    agencyName: '' }),
  computed: {
    // A workspace started for one business is named after it. Before client links go out on Agency, the agency
    // names itself (the API refuses those links until then).
    needsAgencyName() {
      const workspace = this.ws ? this.ws.workspace : null;
      return !!workspace && workspace.kind !== 'agency' && !!(this.limits.client_approvals || this.limits.client_reports)
        && (this.can('reviewers') || this.can('report_links'));
    },
    seatText() {
      const seats = this.team.seats || {};
      return (seats.used || 1) + (seats.allowed ? ' of ' + seats.allowed : '') + ' seats used, including you. Client reviewers don\'t use seats.';
    },
    seatsUsed() { return (this.team.seats && this.team.seats.used) || 1; },
    // A plan with only the owner's seat: the section says what seats do and which plans have them.
    oneSeat() { return (this.limits.seats || 1) <= 1; },
    inviteCap() { return this.capOf('seats', this.seatsUsed); },
    seatsLine() {
      const more = planOrder(this.planMatrix).filter(name => allowsMore(this.planMatrix[name].limits.seats, this.limits.seats));
      const counts = more.map((name, index) => this.planName(name) + (index ? ' ' : ' includes ') + this.planMatrix[name].limits.seats
        + (index ? '' : ' seats'));
      return LOCKED_FEATURES.seats.about + (counts.length ? ' ' + counts.join(', ') + '.' : '');
    },
    approvalsLine() { return LOCKED_FEATURES.client_approvals.about + ' ' + LOCKED_FEATURES.client_reviewers.about; }
  },
  watch: { brandId() { this.freshLink = null; this.freshReport = null; this.loadBrand(); } },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    this.loadTeam();
    this.loadBrand();
    if (this.limits.agency_templates && this.can('templates')) this.loadTemplates();
  },
  methods: {
    possessive,
    initials,
    when: formatRelative,
    roleLabel(role) { return role === 'admin' ? 'Admin' : 'Editor'; },
    scopeText(member) {
      if (member.brand_scope !== 'selected') return 'All brands';
      const names = (member.brand_ids || []).map(id => (this.brands.find(item => item.id === id) || {}).name).filter(Boolean);
      return names.length ? names.join(', ') : 'Selected brands';
    },
    fail(error) {
      const limit = planLimit(error);
      if (limit) { this.inviteOpen = false; this.reviewerOpen = false; this.upgrade = limit; this.upgradeOpen = true; return; }
      this.error = message(error);
      toast(this.error, 'error');
    },
    async loadTeam() {
      if (!this.can('members')) return;
      try { this.team = await get('/members'); } catch (error) { toast(message(error), 'error'); }
    },
    async loadBrand() {
      if (!this.brandId) return;
      try {
        if (this.can('reviewers') && this.limits.client_reviewers) this.reviewers = (await get('/brands/' + this.brandId + '/reviewers')).reviewers || [];
        if (this.can('report_links') && this.limits.client_reports) this.links = (await get('/brands/' + this.brandId + '/report-links')).links || [];
      } catch (error) { toast(message(error), 'error'); }
    },
    async loadTemplates() {
      try { this.templates = (await get('/templates')).templates || []; } catch (error) { this.templates = []; }
    },
    openInvite() {
      // Every seat in use: the upgrade note comes first, not an invitation the plan would refuse.
      const reached = limitReached('seats', this.limits, this.seatsUsed, this.availableOn('seats'));
      if (reached) { this.upgrade = reached; this.upgradeOpen = true; return; }
      this.error = '';
      this.inviteLink = '';
      this.inviteForm = { email: '', role: 'editor', brand_scope: 'all', brand_ids: [] };
      this.inviteOpen = true;
    },
    async invite() {
      this.busy = true;
      this.error = '';
      try {
        const result = await post('/members', this.inviteForm);
        this.inviteLink = result.invite_url;
        this.loadTeam();
        this.refreshWorkspace();
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    async reinvite(member) {
      this.inviteForm = { email: member.email, role: member.role, brand_scope: member.brand_scope, brand_ids: member.brand_ids || [] };
      this.inviteLink = '';
      this.inviteOpen = true;
      await this.invite();
    },
    async changeRole(member, role) {
      try { await patch('/members/' + member.id, { role }); toast('Role changed.'); this.loadTeam(); } catch (error) { this.fail(error); }
    },
    async removeMember(member) {
      if (!(await ask('Remove ' + member.email + '? Their access ends at once.', { action: 'Remove', tone: 'danger' }))) return;
      try { await remove('/members/' + member.id); toast('Removed.'); this.loadTeam(); this.refreshWorkspace(); } catch (error) { this.fail(error); }
    },
    async saveAgencyName() {
      const name = this.agencyName.trim();
      if (!name) return;
      this.busy = true;
      try {
        await patch('/workspace', { name, kind: 'agency' });
        await this.refreshWorkspace();
        this.agencyName = '';
        toast('Saved. Your clients will see ' + withStop(name));
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    async toggleApproval(event) {
      const wanted = event.target.checked;
      try {
        await patch('/brands/' + this.brandId + '/approval', { requires_client_approval: wanted });
        await this.refreshWorkspace();
        toast(wanted ? 'Client approval is on for ' + withStop(this.brand.name) : 'Client approval is off.');
      } catch (error) { event.target.checked = !wanted; this.fail(error); }
    },
    async addReviewer() {
      this.busy = true;
      this.error = '';
      try {
        const reviewer = await post('/brands/' + this.brandId + '/reviewers', { name: this.reviewerForm.name.trim(), email: this.reviewerForm.email.trim() });
        this.freshLink = { url: reviewer.review_url, label: 'Review link for ' + reviewer.name };
        this.reviewerOpen = false;
        this.reviewerForm = { name: '', email: '' };
        this.loadBrand();
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    async rotate(reviewer) {
      if (!(await ask('Make a new link for ' + reviewer.name + '? The old link stops working.', { action: 'Make a new link' }))) return;
      try {
        const fresh = await post('/reviewers/' + reviewer.id + '/rotate');
        this.freshLink = { url: fresh.review_url, label: 'New review link for ' + reviewer.name };
      } catch (error) { this.fail(error); }
    },
    async revoke(reviewer) {
      if (!(await ask('Remove ' + reviewer.name + '? Their link stops working.', { action: 'Remove', tone: 'danger' }))) return;
      try { await remove('/reviewers/' + reviewer.id); this.freshLink = null; this.loadBrand(); toast('Reviewer removed.'); } catch (error) { this.fail(error); }
    },
    async createLink() {
      try {
        const link = await post('/brands/' + this.brandId + '/report-links', { label: this.linkLabel.trim() });
        this.freshReport = link;
        this.linkLabel = '';
        this.loadBrand();
      } catch (error) { this.fail(error); }
    },
    async revokeLink(link) {
      if (!(await ask('Revoke "' + link.label + '"? The page stops working.', { action: 'Revoke', tone: 'danger' }))) return;
      try { await remove('/report-links/' + link.id); this.freshReport = null; this.loadBrand(); toast('Report link revoked.'); } catch (error) { this.fail(error); }
    },
    async deleteTemplate(item) {
      if (!(await ask('Delete the template "' + item.name + '"? Pages and widgets made from it stay.', { action: 'Delete', tone: 'danger' }))) return;
      try { await remove('/templates/' + item.id); this.loadTemplates(); toast('Template deleted.'); } catch (error) { this.fail(error); }
    },
    copied() { toast('Link copied.'); },
    upsell(detail) { this.upgrade = detail; this.upgradeOpen = true; }
  }
};
</script>

<style scoped>
.tm-section{margin-bottom:34px}
.tm-h{margin-bottom:12px}
.tm-list-card{padding:6px 20px}
.tm-member{display:flex;align-items:center;gap:12px;padding:14px 0;border-bottom:1px solid #EFE9E0}
.tm-member:last-child{border-bottom:0}
.tm-who{flex:1;display:flex;flex-direction:column;min-width:0}
.tm-who strong{font-size:14.5px;color:#1F2328;overflow:hidden;text-overflow:ellipsis}
.tm-role{min-height:36px;padding-top:6px;padding-bottom:6px;width:auto}
.tm-none{padding:16px 0}
.tm-roles{margin-top:12px;font-size:14px;color:#4F4A45}
.tm-roles summary{cursor:pointer;min-height:44px;display:inline-flex;align-items:center;font-weight:600;color:#963A22}
.tm-roles ul{margin:6px 0 0;padding-left:18px;line-height:1.6}
.tm-approval{padding:14px 0;border-bottom:1px solid #EFE9E0}
.tm-fresh{padding:14px 0}
.tm-link-form{padding:14px 0;border-bottom:1px solid #EFE9E0}
.tm-link-form .pb-row .pb-input{flex:1}
.tm-agency{margin-bottom:34px}
.tm-agency-form{margin-top:14px}
.tm-agency-form .pb-row .pb-input{flex:1}
.tm-link-icon{display:inline-grid;place-items:center;width:40px;height:40px;border-radius:50%;background:#F3EEE6;color:#963A22;font-size:20px;flex:0 0 40px}
.tm-gap{margin-top:8px!important}
.tm-brands{display:grid;gap:6px;margin:8px 0 0 28px}
.tm-scope{display:flex;flex-wrap:wrap;align-items:center;gap:6px 10px}
@media(max-width:700px){.tm-member{flex-wrap:wrap}.tm-member .pb-actions{width:100%;justify-content:flex-end}}
/* Small phones: the field takes the whole line, so its placeholder isn't cut, and the button goes under it. */
@media(max-width:480px){.tm-link-form .pb-row>*,.tm-agency-form .pb-row>*{flex:1 1 100%}}
</style>
